import { Checkbox } from "react-migration/components/Checkbox/Checkbox";
import { Tooltip } from "react-migration/components/Tooltip";
import { Theme } from "react-migration/lib/theme/Theme";
import { Accordion } from "react-migration/components/Accordion";

import { useState } from "react";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import {
  PropertyTypeOption,
  gatherConditions,
  gatheredConditionsSelectedState,
} from "react-migration/domains/comparables/util/PropertyTypes";
import { emptyArray } from "../../MapLayer/ComparablesLayer/comparablesLayerUtil";
import { HiddenFieldPath } from "./FilterPropertyTypes";
import { DCSCondition } from "react-migration/lib/typings/comparables/dcs";
import clsx from "clsx";

const asKey = (item: DCSCondition) => `${item.designation || ""}_${item.category || ""}_${item.sub_category || ""}`;

/**
 * Checks if an option with the given path should be hidden
 * @param path The path to check in dot notation format (e.g., "Commercial.Other")
 * @param hiddenFields Set of paths that should be hidden
 */
const shouldHideOption = (path: string, hiddenFields?: Set<HiddenFieldPath>): boolean => {
  if (!hiddenFields || hiddenFields.size === 0) return false;
  return Array.from(hiddenFields).some((hiddenPath) => {
    // Exact match
    if (hiddenPath === path) return true;
    // Check if this is a child path of a hidden parent
    // e.g., if "Commercial" is hidden, "Commercial.Retail" should also be hidden
    if (path.startsWith(`${hiddenPath}.`)) return true;
    return false;
  });
};

export interface NestedOptionsProps {
  depth?: number;
  deselect?: (options: DCSCondition[]) => void;
  disabled?: boolean;
  option: PropertyTypeOption;
  openedAccordions: string[];
  selectedOptions: DCSCondition[];
  select?: (options: DCSCondition[]) => void;
  theme?: Theme;
  hiddenFields?: Set<HiddenFieldPath>;
  parentPath?: string;
}

export const NestedOptions = ({
  depth = 0,
  deselect,
  disabled,
  option,
  openedAccordions,
  selectedOptions,
  select,
  theme = Theme.Light,
  hiddenFields,
  parentPath = "",
}: NestedOptionsProps) => {
  const { t } = useTranslation();
  const allConditions = gatherConditions(option);
  const selectState = gatheredConditionsSelectedState(allConditions, selectedOptions);
  const [theseOpenedAccordions, setTheseOpenedAccordions] = useState<string[]>([]);
  const level = ["designation", "category", "subcategory"][depth];

  const id = asKey(option);
  const singular = !option.subTypes?.length;

  // Construct the current option's path in dot notation
  // For the first level (designation), we don't need a parent path
  // For subsequent levels, we append the current value to the parent path
  const optionValue = option[level as keyof DCSCondition] as string;
  const currentPath = depth === 0 ? optionValue : `${parentPath}.${optionValue}`;

  // Check if this option should be hidden based on hiddenFields
  if (shouldHideOption(currentPath, hiddenFields)) {
    return null;
  }
  const label = (
    <label className={clsx("pl-2 w-full", disabled && "text-text-base-primary-disabled border-border-button-disabled")}>
      {t(option.i18nCode)}
      {option.tooltipCode && (
        <Tooltip.Wrapper>
          <Tooltip.Portal>
            <Tooltip.Content side="right" theme={Theme.Light}>
              <Tooltip.Description>{t(option.tooltipCode)}</Tooltip.Description>
            </Tooltip.Content>
          </Tooltip.Portal>
          <Tooltip.Trigger>
            <i className="ml-2 icon-lt-information-line" />
          </Tooltip.Trigger>
        </Tooltip.Wrapper>
      )}
    </label>
  );
  return (
    <Accordion.Item value={id} className={clsx("flex flex-col", depth > 0 && "pl-2")} key={id}>
      <div
        className={clsx("flex items-center flex-1 py-1 px-2 rounded-lg", !disabled && "hover:backdrop-brightness-90")}
      >
        <Checkbox
          disabled={disabled}
          id={id}
          supportsIndeterminate={true}
          checked={selectState === "indeterminate" ? "indeterminate" : selectState === "checked"}
          onCheckedChange={() => (selectState === "checked" ? deselect?.(allConditions) : select?.(allConditions))}
        />
        {singular ? (
          <div className="py-1">{label}</div>
        ) : (
          <Accordion.Trigger disabled={disabled} className="flex justify-end items-center bg-transparent!">
            <div className="flex-1 text-left">{label}</div>
            <div className="flex items-center rounded-sm pointer-events-none color-dark" aria-hidden="true">
              <i
                className={`icon-lt-arrow-up-s-line text-lg duration-150${
                  openedAccordions.some((item) => item === id) ? "" : " rotate-180"
                }`}
              />
            </div>
          </Accordion.Trigger>
        )}
      </div>
      {singular ? null : (
        <Accordion.Content>
          <Accordion
            type="multiple"
            disabled={disabled}
            onValueChange={(value) => setTheseOpenedAccordions(value)}
            className={`flex ${level === "subcategory" ? "flex-row flex-wrap" : "flex-col"}`}
          >
            {(option.subTypes ?? []).map((option) => {
              return (
                <NestedOptions
                  disabled={disabled}
                  key={asKey(option)}
                  depth={depth + 1}
                  deselect={deselect}
                  openedAccordions={disabled ? emptyArray : theseOpenedAccordions}
                  option={option}
                  selectedOptions={selectedOptions}
                  select={select}
                  theme={theme}
                  hiddenFields={hiddenFields}
                  parentPath={currentPath}
                />
              );
            })}
          </Accordion>
        </Accordion.Content>
      )}
    </Accordion.Item>
  );
};
